import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { getSideListOptionsBottomFn } from '@fi-shared/fi-items-list/side-list-options';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { MediaBankService } from '../media-bank.service';
import { MediaBankListData, MediaBankListDataService } from '../media-bank-list-data.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { IItemsListParams, ItemsListContainerBase } from '@fi-app/components/items-list-container-base';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { FiLocalSearchParameterModel } from '@fi-shared/fi-local-search2/fi-local-search-parameter.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiTagsListPositionTypes } from '@fi-app/components/items-list/enums';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { getMediaBankModulesForLocalSearch } from '../media-bank-states';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ItemsListComponent } from '@fi-app/components/items-list/items-list.component';
import { isNullOrUndefined } from 'util';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { TabsModuleService } from '@fi-app/utilities/tabs/tabs-module.service';
import { FiMediaBankService } from '@fi-shared/media-bank';
import { FiLocalSearchGroupType } from '@fi-shared/fi-local-search2/fi-local-search-group-type';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';

@Component({
    selector: 'fi-media-bank-list-view',
    templateUrl: './media-bank-list-view.component.html',
    styleUrls: ['./media-bank-list-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-media-bank-list-view' },
    standalone: false,
})
export class FiMediaBankListViewComponent
    extends ItemsListContainerBase<MediaBankListData>
    implements OnInit, OnDestroy
{
    @Input() mediaListViewSettings: ListViewSettingModel;
    @Input() isSideListShown: boolean = true;
    @Input() isItemActionsShown: boolean = false;
    @Input() listParams: IItemsListParams;
    @Input() tagIds: number[] = [];
    @Input() isFavoriteIconShown: boolean = true;
    @ViewChild(ItemsListComponent, { static: false }) itemsListComponent: ItemsListComponent;
    MediaBankFileOptionsBottom: ISideListOptionsItem[] = [];
    MediaBankFileOptionsTop: ISideListOptionsItem[] = [];
    MediaBankFileOptionsTags: ISideListOptionsItem[] = [];
    ShowTagsSidebar: any;
    FiStateNames = FiStateNames;

    modulesForLocalSearch = getMediaBankModulesForLocalSearch();

    constructor(
        private mediaBankService: MediaBankService,
        private fiMediaBankService: FiMediaBankService,
        private mediaBankListDataService: MediaBankListDataService,
        private tabsModuleService: TabsModuleService,
        private detectDevice: DetectDevicesService,
        private stateAccessCheckService: StateAccessCheckService,
        private appTextService: AppTextService,
        private mediaStorageService: FiMediaStorageService,
        private stateService: FiStateService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit() {
        this.reloadListSubscription();

        if (isNullOrUndefined(this.listParams)) this.listParams = Object.assign({}, { showDelete: false });

        this.listParams.defaultFilterTypeFn = () => this.MediaBankFileOptionsTop[0].value;
        super.onInit(() => {
            return this.mediaBankListDataService.getInstance({
                isFavoriteIconShown: this.isFavoriteIconShown,
            });
        }, this.listParams);

        this.ListData.ListViewSettings = this.mediaListViewSettings;

        this.ShowTagsSidebar =
            this.detectDevice.isMobilePhone() ||
            this.mediaListViewSettings.PositionType === FiTagsListPositionTypes.Left;

        //this._initSearch();

        if (this.ListData.Statefull) {
            const shouldGoToAll =
                this.stateService.params.filterType == SideListOptionType.OnlyNotSeen &&
                this.stateService.isHistoryBack &&
                this.fiSignalRHelperService.getNewMediaFileIdsCount() === 0;
            if (shouldGoToAll) super.SideListItemClick(SideListOptionType.SharedWithMe);
        }

        this.ListData.ListViewSettings.prepareTagsForList(() => {
            return this.mediaBankService.GetMediaBankFileTags();
        }).then(() => {
            this.initSideList();
        });

        this.detectDisplayTypeChange$();

        this.tabsModuleService.setTabsSettings({
            listData: this.ListData,
        });
    }

    private initSideList(): void {
        this.MediaBankFileOptionsTop = [
            { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
            { value: SideListOptionType.New, text: this.appTextService.get('common.new_files') },
            { value: SideListOptionType.OnlyNotVisited, text: this.appTextService.get('mediabank.unread_files') },
            { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
            { value: SideListOptionType.Favorite, text: this.appTextService.get('common.my_favorites') },
            { value: SideListOptionType.Deleted, text: this.appTextService.get('common.trash') },
        ];
        super.checkApplySuperAdminRight(
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.MediaBankGlobal,
                AccessRightType.SuperAdmin,
            ),
            this.MediaBankFileOptionsTop,
            this.appTextService.get('mediaModule.all_files'),
        );

        this.MediaBankFileOptionsBottom = getSideListOptionsBottomFn(this.appTextService);
        this.MediaBankFileOptionsTags = this.getSideListOptionsTags();
    }

    private detectDisplayTypeChange$() {
        this.ListData.displayTypeChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.cdr.markForCheck();
        });
    }

    private reloadListSubscription() {
        this.mediaBankService.reloadMediaBankList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.ListData.clearData();
            this.ListData.Initialize();
        });
    }

    IsTagSelected(tagId) {
        return this.ListData.selectedTags && this.ListData.selectedTags.indexOf(tagId) >= 0;
    }

    GetMediaBankFileParameter() {
        const mediaBankFileParameter = new FiLocalSearchParameterModel();
        Object.assign(mediaBankFileParameter, this.ListData.getFilterParams());

        Object.assign(mediaBankFileParameter, this.ListData.getOffsetParams());

        mediaBankFileParameter.SelectedSort = {
            ColumnName: this.ListData.Sort.Column,
            SortDirection: this.ListData.Sort.Reverse,
        };

        mediaBankFileParameter.TagIds = this.tagIds.slice();
        mediaBankFileParameter.FilterType = this.ListData.FilterType;

        if (this.ListData.selectedTags)
            this.ListData.selectedTags.forEach((tagId) => mediaBankFileParameter.TagIds.push(tagId));

        this.ListData.mergeLocalSearchV2Parameter(mediaBankFileParameter);

        /*mediaBankFileParameter.CreatedByUserNames = this.localSearch.getAddedNames(FiLocalSearchGroupType.Creators);
        mediaBankFileParameter.Names = this.localSearch.getAddedNames(FiLocalSearchGroupType.Names);
        mediaBankFileParameter.ContentSearchTexts = this.localSearch.getAddedNames(FiLocalSearchGroupType.ContentSearch);
        mediaBankFileParameter.KeywordsSearchTexts = this.localSearch.getAddedNames(FiLocalSearchGroupType.KeywordsSearch); // todo search by keywords?
        mediaBankFileParameter.BusinessAreaIds = this.localSearch.getAddedIds(FiLocalSearchGroupType.BusinessAreas);
        mediaBankFileParameter.TagIds.push(...this.localSearch.getAddedIds(FiLocalSearchGroupType.Tags)
            .filter(tagId => mediaBankFileParameter.TagIds.indexOf(tagId) === -1));

        if (this.WildcardSearch) {
            //Adding wild card to content later on it will be divided into name and content wild card
            mediaBankFileParameter.ContentSearchTexts.push(this.WildcardSearch);
        }*/

        return mediaBankFileParameter;
    }

    /* protected _initSearch() {
        /!*this.localSearch.addSearchGroup(FiLocalSearchGroupType.KeywordsSearch, {
            Header: this.appTextService.get('common.Keywords'),
            ObjectType: ObjectTypes.MediaBank,
            Single: true
        });*!/

        this.localSearch.addSearchGroup(FiLocalSearchGroupType.ContentSearch, {
            Header: this.appTextService.get('common.content'),
            ObjectType: -1,
            WildcardSearch: true
        });
        this.localSearch.addSearchGroup(FiLocalSearchGroupType.KeywordsSearch, {
            Header: this.appTextService.get('common.Keywords'),
            ObjectType: -2,
            WildcardSearch: true
        });
    }*/

    SetMediaBankFilesCountBySelectedFilterType() {
        return this.mediaBankService.GetMediaBankFilesCount(this.GetMediaBankFileParameter());
    }

    /*private initializeLocalSearchData(lightWeightObjectCollection: ILightWeightObjectCollection)  {
        this.localSearch.addSearchGroupDataStr(FiLocalSearchGroupType.Names, lightWeightObjectCollection.Names);
        this.localSearch.addSearchGroupDataStr(FiLocalSearchGroupType.Creators, lightWeightObjectCollection.Creators);
        this.localSearch.addSearchGroupData(FiLocalSearchGroupType.Tags, lightWeightObjectCollection.Tags);
        this.localSearch.addSearchGroupData(FiLocalSearchGroupType.BusinessAreas, lightWeightObjectCollection.BusinessAreas);
    }*/

    protected LoadItems(): Promise<void> {
        const fetchingAllData = this.ListData.fetchingAllData;
        const parameter = this.GetMediaBankFileParameter();
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: fetchingAllData,
            FetchLightWeightObjects: fetchingAllData,
        };
        return this.fiMediaBankService.getMediaBankFilesListData(parameter).then((response) => {
            this.ListData.appendData(this.mediaBankService.getMediaBankImages(response.Items));

            if (fetchingAllData) {
                this.ListData.SetTotalItemsCount(response.TotalCount);

                const lightWeightObjectCollection = response.LightWeightObjectsForLocalSearch;
                if (lightWeightObjectCollection) {
                    this.ListData.raiseLightWeightObjectCollectionChanged(lightWeightObjectCollection);
                }
            }
        });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        super.ngOnDestroy();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
